import type { Ref } from 'vue';
import type { Entitlement, MonospacePolicyEntitlementResource, PolicyEntitlementAction } from '#layers/access/types/permissions/policy';
import type { EntitlementResourceId } from '#layers/access/utils/entitlement-codec';
import { anyResourceTarget, decodeEntitlementKey, encodeEntitlementKey, entitlementKeyMatchesAction, EntitlementTargetKinds, oneResourceTarget, removeEntitlementKey, unscopedTarget } from '#layers/access/utils/entitlement-codec';

interface UsePolicyEntitlementsOptions {
	entitlements: Ref<Entitlement[]>;
}

export function usePolicyEntitlements({ entitlements }: UsePolicyEntitlementsOptions) {
	return {
		addEntitlementWildcard,
		addResourceSelection,
		setEntitlements,
		removeEntitlement,
		hasWildcardEntitlement,
		enabledActionsForResource,
		selectedResourceIdsForEntitlement,
		hasEntitlement,
		removeResourceSelection,
	};

	function addEntitlementWildcard(resource: MonospacePolicyEntitlementResource, action: PolicyEntitlementAction) {
		const newEntitlements = filterEntitlements(resource, action);
		newEntitlements.push(encodeEntitlementKey({ resource: resource.name, action: action.name, target: action.hasIdentifier ? anyResourceTarget : unscopedTarget }));
		entitlements.value = newEntitlements;
	}

	function setEntitlements(changedEntitlements: Entitlement[], resource: MonospacePolicyEntitlementResource, action: PolicyEntitlementAction) {
		const newEntitlements = filterEntitlements(resource, action);
		newEntitlements.push(...changedEntitlements);
		entitlements.value = newEntitlements;
	}

	function removeEntitlement(resource: MonospacePolicyEntitlementResource, action: PolicyEntitlementAction) {
		entitlements.value = filterEntitlements(resource, action);
	}

	function removeResourceSelection(resource: MonospacePolicyEntitlementResource, action: PolicyEntitlementAction, resourceId: EntitlementResourceId) {
		entitlements.value = filterEntitlements(resource, action, resourceId);
	}

	function hasWildcardEntitlement(resource: MonospacePolicyEntitlementResource, action: PolicyEntitlementAction) {
		return entitlements.value?.includes(encodeEntitlementKey({ resource: resource.name, action: action.name, target: anyResourceTarget }));
	}

	function hasEntitlement(resource: MonospacePolicyEntitlementResource, action: PolicyEntitlementAction) {
		if (hasWildcardEntitlement(resource, action)) {
			return true;
		}

		return entitlements.value?.some((entitlement) => entitlementKeyMatchesAction(entitlement, { resource: resource.name, action: action.name })) ?? false;
	}

	function enabledActionsForResource(resource: MonospacePolicyEntitlementResource) {
		return resource.actions.filter((action) => hasEntitlement(resource, action));
	}

	function selectedResourceIdsForEntitlement(resource: MonospacePolicyEntitlementResource, action: PolicyEntitlementAction): EntitlementResourceId[] {
		return (entitlements.value ?? [])
			.filter((entitlement) => entitlementKeyMatchesAction(entitlement, { resource: resource.name, action: action.name }))
			.map((entitlement) => decodeEntitlementKey(entitlement).target)
			.flatMap((target) => target.kind === EntitlementTargetKinds.OneResource ? [target.id] : []);
	}

	function addResourceSelection(resource: MonospacePolicyEntitlementResource, action: PolicyEntitlementAction, resourceId: EntitlementResourceId) {
		const key = encodeEntitlementKey({ resource: resource.name, action: action.name, target: oneResourceTarget(resourceId) });

		if (entitlements.value?.includes(key)) {
			return;
		}

		entitlements.value = [...entitlements.value ?? [], key];
	}

	function filterEntitlements(resource: MonospacePolicyEntitlementResource, action: PolicyEntitlementAction, resourceId?: EntitlementResourceId) {
		const values = entitlements.value ?? [];

		if (resourceId) {
			return removeEntitlementKey(values, { resource: resource.name, action: action.name, target: oneResourceTarget(resourceId) });
		}

		return values.filter((entitlement) => !entitlementKeyMatchesAction(entitlement, { resource: resource.name, action: action.name }));
	}
}
